import {useEffect, useRef, useState} from "react";
import cx from "classnames";
import {Button} from "@varymadellc/my-weed-club-component-library";
import Loading from "@/components/Loading/Loading";

export default function AudioPlayer({src, className}) {
    const audioPlayer = useRef<any>(null); // Reference to the audio element
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    // Update duration when metadata is loaded
    useEffect(() => {
        const audio = audioPlayer.current;

        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
        };

        const setAudioTime = () => setCurrentTime(audio.currentTime);

        audio.addEventListener('loadeddata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);

        if (isPlaying) {
            requestAnimationFrame(onPlaying);
        }

        return () => {
            audio.removeEventListener('loadeddata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
        };
    }, [isPlaying]);

    // Toggle play/pause
    const togglePlayPause = async () => {
        const prevValue = isPlaying;

        setIsPlaying(!prevValue);

        if (!prevValue) {
            await audioPlayer.current.play();
        } else {
            await audioPlayer.current.pause();
        }
    };

    // Update currentTime
    const onPlaying = () => {
        if (audioPlayer?.current) {
            setCurrentTime(audioPlayer.current.currentTime);
            requestAnimationFrame(onPlaying);
        }
    };

    // Seek functionality
    const onSeek = (e) => {
        audioPlayer.current.currentTime = e.target.value;
        setCurrentTime(audioPlayer.current.currentTime);
        if (!isPlaying) {
            audioPlayer.current.play();
            setIsPlaying(true);
        }
    };

    // Format time for display
    const formatTime = (time) => {
        let minutes = Math.floor(time / 60);
        let seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <div className={cx('custom-audio-player flex flex-row items-center gap-4 relative', className)}>
            {
                !src && <Loading className="absolute z-[1000] top-0 bottom-0 left-0 right-0 flex justify-center items-center bg-black/80 rounded"/>
            }
            <audio ref={audioPlayer} src={src} preload="metadata"/>
            <div className="w-full flex flex-row h-10 justify-center items-center gap-2 p-2 bg-black rounded my-4">
                <Button className="flex-1 h-full p-0 line-height-0 min-w-[100px]"
                        onClick={togglePlayPause}>{isPlaying ? 'Pause' : 'Play'}</Button>
                <input className="h-4 flex flex-[6]" type="range" value={currentTime} max={duration} onChange={onSeek}/>
                <div className="p-2 flex flex-1">
                    <span
                        className="break-keep whitespace-nowrap text-xs">{formatTime(currentTime)} / {formatTime(duration)}</span>
                </div>
            </div>
        </div>
    );
};